/* eslint-disable complexity */
import Button from 'carbon-react/lib/components/button';
import Form from 'carbon-react/lib/components/form';
import Sidebar from 'carbon-react/lib/components/sidebar';
import Typography from 'carbon-react/lib/components/typography';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { FormattedMessage } from '@futrli/localisation';
import { toastError, UILoader } from '@futrli/ui-components';

import { exportForecastToExcel } from '../../../../../core/api/external-sheets';

interface Props {
  onClose: () => void;
  forecastId?: string;
}

const ALLOWED_DOWNLOAD_PROTOCOLS = new Set(['blob:', 'http:', 'https:']);

const isAllowedDownloadURL = (downloadURL: string) => {
  try {
    return ALLOWED_DOWNLOAD_PROTOCOLS.has(new URL(downloadURL).protocol);
  } catch {
    return false;
  }
};

const startDownload = (downloadURL: string) => {
  const link = document.createElement('a');
  link.href = downloadURL;
  link.download = '';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const ExcelExportDrawer: FC<Props> = ({ onClose, forecastId: propsForecastId }) => {
  const { workspaceId = '', organisationId = '', forecastId = '' } = useParams();
  const [downloadURL, setDownloadURL] = useState('');
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    let isCurrentExport = true;

    setDownloadURL('');

    const handleExport = async () => {
      try {
        const forecastIdToUse = forecastId || propsForecastId;

        if (!workspaceId || !organisationId || !forecastIdToUse) {
          throw new Error('Workspace, organisation and forecast IDs are required for an Excel export');
        }

        const result = await exportForecastToExcel(workspaceId, organisationId, forecastIdToUse);

        if (!isCurrentExport) {
          return;
        }

        if (!result?.download_url || !isAllowedDownloadURL(result.download_url)) {
          throw new Error('Excel export did not return a valid download URL');
        }

        setDownloadURL(result.download_url);
      } catch {
        if (!isCurrentExport) {
          return;
        }

        toastError();
        onCloseRef.current();
      }
    };

    handleExport();

    return () => {
      isCurrentExport = false;
    };
  }, [forecastId, organisationId, propsForecastId, workspaceId]);

  const handleDownload = () => {
    if (!isAllowedDownloadURL(downloadURL)) {
      toastError();
      return;
    }

    startDownload(downloadURL);
    onClose();
  };

  return (
    <Sidebar
      header={
        <Typography variant="h2" fontWeight="700">
          <FormattedMessage id="predict.export_report" defaultMessage="Export report" />
        </Typography>
      }
      onCancel={onClose}
      open={true}
    >
      {downloadURL ? (
        <Form
          stickyFooter
          leftSideButtons={
            <Button onClick={onClose} data-testid="excel-export-dialog-actions-secondary">
              <FormattedMessage id="common.close" defaultMessage="Close" />
            </Button>
          }
          saveButton={
            <Button
              buttonType="primary"
              onClick={handleDownload}
              data-testid="excel-export-dialog-actions-download"
            >
              <FormattedMessage id="common.download" defaultMessage="Download" />
            </Button>
          }
          onSubmit={handleDownload}
        >
          <p data-testid="excel-export-dialog-complete">
            <FormattedMessage
              id="predict.report_successfully_generated_available_to_download"
              defaultMessage="Your report has been successfully generated and is available to download below."
            />
          </p>
        </Form>
      ) : (
        <UILoader />
      )}
    </Sidebar>
  );
};
